<script setup lang="ts">
import { ESection } from '@/components/base/Email'
import EmailBlockWrapper from './EmailBlockWrapper.vue'
import EmailGalleryToolbar from './EmailGalleryToolbar.vue'
import EmailGalleryCell from './EmailGalleryCell.vue'
import type { GalleryBlock, GalleryItem } from '../../types'

const props = defineProps<{
  block: GalleryBlock
  isSelected: boolean
  selectedCellIndex: number | null
  uploadingItemIds?: Set<number>
  colorPresets?: { color: string; label: string }[]
}>()

const emit = defineEmits<{
  (e: 'select'): void
  (e: 'update:selectedCellIndex', index: number | null): void
  (e: 'upload-file', payload: { index: number; file: File }): void
  (e: 'update:block', block: GalleryBlock): void
}>()

const { t } = useI18n()

const onCellSelect = (index: number) => {
  if (!props.isSelected) emit('select')
  emit('update:selectedCellIndex', index)
}

const updateItem = (index: number, item: GalleryItem) => {
  const items = props.block.items.slice()
  items[index] = item
  emit('update:block', { ...props.block, items })
}

const removeItem = (index: number) => {
  const items = props.block.items.slice()
  items.splice(index, 1)
  if (props.selectedCellIndex !== null) {
    if (props.selectedCellIndex === index) {
      emit('update:selectedCellIndex', null)
    } else if (props.selectedCellIndex > index) {
      emit('update:selectedCellIndex', props.selectedCellIndex - 1)
    }
  }
  emit('update:block', { ...props.block, items })
}

const isFixedLayout = computed(() => props.block.layout !== 'grid')

const effectiveColumns = computed(() => Math.min(props.block.items.length, props.block.columns) || 1)

const gridFlexBasis = computed(() => {
  const cols = effectiveColumns.value
  return `calc(${100 / cols}% - ${(props.block.gap * (cols - 1)) / cols}px)`
})

watch(
  () => props.block.items.length,
  (length) => {
    if (props.selectedCellIndex !== null && props.selectedCellIndex >= length) {
      emit('update:selectedCellIndex', null)
    }
  }
)

const item = (index: number): GalleryItem | undefined => props.block.items[index]

const cropAspectRatio = (index: number): number | [number, number] | undefined => {
  if (props.block.layout === 'asymmetric-right') return index === 2 ? [1, 2] : 1
  return props.block.columns === 1 ? undefined : 1
}
</script>

<template>
  <EmailBlockWrapper
    :is-selected="isSelected"
    :label="t('pages.email.emailTemplateCreatePage.blockGallery')"
    cursor="pointer"
    :block-background-color="block.blockBackgroundColor"
    @select="emit('select')"
  >
    <EmailGalleryToolbar
      v-if="isSelected && selectedCellIndex === null"
      :block="block"
      :is-fixed-layout="isFixedLayout"
      :color-presets="colorPresets"
      @update:block="emit('update:block', $event)"
    />

    <ESection :style="{ padding: '10px 0' }">
      <div v-if="block.layout === 'grid'" class="flex flex-wrap" :style="{ gap: `${block.gap}px` }">
        <div
          v-for="(galleryItem, index) in block.items"
          :key="index"
          class="min-w-0 flex-shrink-0 flex-grow-0"
          :style="{ flexBasis: gridFlexBasis }"
        >
          <EmailGalleryCell
            :index="index"
            :item="galleryItem"
            :is-uploading="!!uploadingItemIds?.has(index)"
            :is-selected="isSelected"
            :is-cell-selected="selectedCellIndex === index"
            :can-delete="block.items.length > 1"
            :can-switch-type="true"
            :crop-aspect-ratio="cropAspectRatio(index)"
            :color-presets="colorPresets"
            @upload-file="(file) => emit('upload-file', { index, file })"
            @update="(updated) => updateItem(index, updated)"
            @remove="removeItem(index)"
            @select="onCellSelect(index)"
          />
        </div>
      </div>

      <div v-else-if="block.layout === 'asymmetric-right'" class="grid grid-cols-2" :style="{ gap: `${block.gap}px` }">
        <div class="flex flex-col" :style="{ gap: `${block.gap}px` }">
          <div v-for="i in [0, 1]" :key="i">
            <EmailGalleryCell
              v-if="item(i)"
              :index="i"
              :item="item(i)!"
              :is-uploading="!!uploadingItemIds?.has(i)"
              :is-selected="isSelected"
              :is-cell-selected="selectedCellIndex === i"
              :can-delete="false"
              :can-switch-type="true"
              :crop-aspect-ratio="cropAspectRatio(i)"
              :color-presets="colorPresets"
              @upload-file="(file) => emit('upload-file', { index: i, file })"
              @update="(updated) => updateItem(i, updated)"
              @remove="removeItem(i)"
              @select="onCellSelect(i)"
            />
          </div>
        </div>
        <div>
          <EmailGalleryCell
            v-if="item(2)"
            :index="2"
            :item="item(2)!"
            :is-uploading="!!uploadingItemIds?.has(2)"
            :is-selected="isSelected"
            :is-cell-selected="selectedCellIndex === 2"
            :can-delete="false"
            :can-switch-type="true"
            :crop-aspect-ratio="cropAspectRatio(2)"
            :color-presets="colorPresets"
            @upload-file="(file) => emit('upload-file', { index: 2, file })"
            @update="(updated) => updateItem(2, updated)"
            @remove="removeItem(2)"
            @select="onCellSelect(2)"
          />
        </div>
      </div>
    </ESection>
  </EmailBlockWrapper>
</template>
